import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import socket from "../socket";
import chatApi from "../api/chatApi";

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const [openChats, setOpenChats] = useState([]); // [ { proposalId, proposal, isCollapsed } ]
  const [unreadCounts, setUnreadCounts] = useState({}); // { [proposalId]: number }
  const openChatsRef = useRef(openChats);

  useEffect(() => {
    openChatsRef.current = openChats;
  }, [openChats]);

  // Fetch initial unread counts from backend
  const fetchUnreadCounts = useCallback(async () => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    if (!token) return;

    try {
      const { data } = await chatApi.getUnreadSummary();
      if (data?.unreadByProposal) {
        setUnreadCounts(data.unreadByProposal);
      }
    } catch (err) {
      console.warn("Failed to fetch unread chat counts:", err?.message);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  // Socket event listener for real-time incoming messages & notifications
  useEffect(() => {
    const handleIncomingMessage = (msg) => {
      const pId = msg?.proposalId;
      if (!pId) return;

      const currentOpen = openChatsRef.current;
      const activeChat = currentOpen.find((c) => c.proposalId === pId);

      // If the chat box is open AND currently expanded, the message is read in real time
      if (activeChat && !activeChat.isCollapsed) {
        return;
      }

      // Otherwise (chat is collapsed or not currently opened), increment unread count
      setUnreadCounts((prev) => ({
        ...prev,
        [pId]: (prev[pId] || 0) + 1,
      }));
    };

    socket.on("new_message", handleIncomingMessage);
    socket.on("chat_notification", handleIncomingMessage);

    return () => {
      socket.off("new_message", handleIncomingMessage);
      socket.off("chat_notification", handleIncomingMessage);
    };
  }, []);

  // Screen width constraint handler: ensure chat boxes never compress adjacent boxes
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 640;
      if (isMobile) {
        setOpenChats((prev) => (prev.length > 1 ? [prev[prev.length - 1]] : prev));
      } else {
        const CHAT_BOX_WIDTH = 340;
        const GAP = 12;
        const MARGIN = 32;
        const maxAllowed = Math.max(
          1,
          Math.floor((window.innerWidth - MARGIN) / (CHAT_BOX_WIDTH + GAP))
        );
        setOpenChats((prev) => {
          if (prev.length > maxAllowed) {
            return prev.slice(prev.length - maxAllowed);
          }
          return prev;
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const markProposalAsRead = useCallback((proposalId) => {
    if (!proposalId) return;
    setUnreadCounts((prev) => {
      if (!prev[proposalId]) return prev;
      return { ...prev, [proposalId]: 0 };
    });
  }, []);

  const openChat = useCallback((proposal) => {
    const pId = proposal?._id;
    if (!pId) return;

    const isMobile = window.innerWidth < 640;

    if (isMobile) {
      // In mobile, only 1 chat box allowed; replace current
      setOpenChats([{
        proposalId: pId,
        proposal,
        isCollapsed: false,
        isProfessionalView: Boolean(proposal.isProfessionalView),
      }]);
    } else {
      const CHAT_BOX_WIDTH = 340;
      const GAP = 12;
      const MARGIN = 32;
      const maxAllowed = Math.max(
        1,
        Math.floor((window.innerWidth - MARGIN) / (CHAT_BOX_WIDTH + GAP))
      );

      setOpenChats((prev) => {
        let filtered = prev.filter((c) => c.proposalId !== pId);
        if (filtered.length >= maxAllowed) {
          // Remove the oldest (leftmost) chat box so existing boxes never shrink
          filtered = filtered.slice(filtered.length - maxAllowed + 1);
        }
        return [
          ...filtered,
          {
            proposalId: pId,
            proposal,
            isCollapsed: false,
            isProfessionalView: Boolean(proposal.isProfessionalView),
          },
        ];
      });
    }

    markProposalAsRead(pId);
  }, [markProposalAsRead]);

  const closeChat = useCallback((proposalId) => {
    if (!proposalId) return;
    const targetIdStr = String(proposalId);
    setOpenChats((prev) => prev.filter((c) => String(c.proposalId) !== targetIdStr));
  }, []);

  const toggleCollapse = useCallback((proposalId, forceState) => {
    if (!proposalId) return;
    const targetIdStr = String(proposalId);
    setOpenChats((prev) =>
      prev.map((c) => {
        if (String(c.proposalId) === targetIdStr) {
          const nextState = typeof forceState === "boolean" ? forceState : !c.isCollapsed;
          // If expanding, mark as read
          if (!nextState) {
            markProposalAsRead(proposalId);
          }
          return { ...c, isCollapsed: nextState };
        }
        return c;
      })
    );
  }, [markProposalAsRead]);

  const getUnreadCount = useCallback(
    (proposalId) => unreadCounts[proposalId] || 0,
    [unreadCounts]
  );

  return (
    <ChatContext.Provider
      value={{
        openChats,
        openChat,
        closeChat,
        toggleCollapse,
        unreadCounts,
        getUnreadCount,
        markProposalAsRead,
        fetchUnreadCounts,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export default ChatContext;
